<template>
    <div v-if="loaded">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="2">
                        <rkm-form-select
                            :options="form.anos"
                            :validations="validations.ano"
                            @change="loadFrom('ano')"
                            label="Ano"
                            show-default-option
                            text-field="ano"
                            v-model="model.ano"
                            value-field="ano"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-select
                            :disabled="!model.ano"
                            :options="form.competencias"
                            :validations="validations.competencia"
                            @change="loadFrom('competencia')"
                            label="Mês"
                            show-default-option
                            text-field="mes"
                            v-model="model.competenciaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="!model.competenciaId"
                            :options="form.unidades"
                            @change="loadFrom('unidade')"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="descricao"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="!model.competenciaId"
                            :options="form.ocupacoes"
                            @change="loadFrom('ocupacao')"
                            default-option-text="Todas"
                            label="CBO"
                            show-default-option
                            text-field="descricao"
                            v-model="model.ocupacaoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="!model.competenciaId"
                            :options="form.usuarios"
                            @change="loadFrom('usuario')"
                            default-option-text="Todos"
                            label="Profissional"
                            show-default-option
                            text-field="descricao"
                            v-model="model.usuarioId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="!model.competenciaId"
                            :options="form.procedimentos"
                            @change="loadFrom('procedimento')"
                            default-option-text="Todos"
                            label="Procedimento"
                            show-default-option
                            text-field="descricao"
                            v-model="model.procedimentoCodigo"
                            value-field="codigo"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="!model.competenciaId"
                            :options="form.cids"
                            @change="loadFrom('cid')"
                            default-option-text="Todos"
                            label="CID"
                            show-default-option
                            text-field="descricao"
                            v-model="model.cidId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="2">
                        <rkm-form-select
                            :disabled="!model.competenciaId"
                            :options="form.inconsistencias"
                            default-option-text="Todas"
                            label="Inconsistência"
                            show-default-option
                            v-model="model.comInconsistencia"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="exportTo"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :items="itens"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                :tbody-tr-class="rowClass"
                @sort-changed="onSortChanged"
                class="table-resultado"
            >
                <template #cell(idade)="{ value }">
                    {{ isNaN(parseInt(value)) ? '-' : value }}
                </template>

                <template #cell(cidDescricao)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(actions)="{ item }">
                    <rkm-button
                        :disabled="!item.comInconsistenciaValue"
                        @click="showInconsistencias(item)"
                        color="primary"
                        small
                        title="Visualizar inconsistências"
                    >
                        <rkm-icon icon="search" />
                    </rkm-button>
                </template>

                <template
                    v-if="itens.length > 0"
                    #afterTable
                >
                    <div class="text-center table-footer">
                        Total de procedimentos: {{ getSafely(() => totalizadores.quantidade, 0) }}
                    </div>
                </template>
            </rkm-table>
        </rkm-area>

        <ModalInconsistencia ref="ModalInconsistencia" />
    </div>
</template>

<script>
// Components.
import ModalInconsistencia from './components/inconsistencia/Modal'

// Vuex.
import { mapActions } from 'vuex'

// Mixins.
import { repository, error, object, stringCase, file, formats } from '@/mixins'

export default {
    components: { ModalInconsistencia },
    computed: {
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        validations() {

            return {
                ano: { required: true },
                competencia: { required: true },
            }
        },
    },
    created() {

        this.repositories = {
            relatorioBpaIndividualizado: this.getConectaSusRepository('bpa.relatorioBpaIndividualizado'),
            impressao: this.getConectaSusRepository('impressao.bpa'),
            exportacao: this.getConectaSusRepository('exportacao.bpa'),
        }

        this.load()
    },
    data() {

        return {
            loaded: false,
            additionals: null,
            form: null,
            model: null,
            initials: {
                form: null,
                model: null,
            },
            selectedAll: true,
            indeterminate: false,
            selectedCols: [
                'unidadeDescricao',
                'ocupacaoDescricao',
                'usuarioDescricao',
                'idade',
                'cidDescricao',
                'dataAtendimento',
                'comInconsistencia',
                'origem',
            ],
            sortBy: 'procedimentoDescricao',
            sortDesc: false,
            columns: [
                { key: 'procedimentoDescricao', label: 'Procedimento', sortable: true, fixed: true },
                { key: 'unidadeDescricao', label: 'Unidade', sortable: true },
                { key: 'ocupacaoDescricao', label: 'CBO', sortable: true },
                { key: 'usuarioDescricao', label: 'Profissional', sortable: true },
                { key: 'idade', label: 'Idade', sortable: true, class: 'col-min text-center' },
                { key: 'cidDescricao', label: 'CID', sortable: true },
                {
                    key: 'dataAtendimento',
                    label: 'Data do atendimento',
                    formatter: v => v ? this.formatDate(v) : null,
                    sortable: true,
                    class: 'col-min text-center'
                },
                { key: 'comInconsistencia', label: 'Inconsistência', sortable: true, class: 'col-min text-center' },
                { key: 'origem', label: 'Origem', sortable: true },
                { key: 'quantidade', label: 'Quantidade', class: 'col-min text-center', sortable: true, fixed: true },
                { key: 'actions', label: 'Ações', class: 'col-min text-center', fixed: true },
            ],
            itens: [],
            totalizadores: null,
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        onSortChanged(context) {

            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
        load() {

            this.setLoading()

            this.repositories.relatorioBpaIndividualizado.list()
                .then(({ data }) => {

                    this.form = data.form
                    this.model = data.model

                    this.initials.form = this.clone(data.form)
                    this.initials.model = this.clone(data.model)

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadFrom(field) {

            this.clearFrom(field)

            this.setLoading()

            this.repositories.relatorioBpaIndividualizado.list({ ...this.prepareData(), load_from: this.camelToSnake(field) })
                .then(({ data }) => {

                    if ('form' in data) {

                        Object.keys(data.form).forEach(key => this.$set(this.form, key, data.form[key]))
                    }

                    Object.keys(data.model).forEach(key => this.$set(this.model, key, data.model[key]))
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        clearFrom(field) {

            if (field === 'procedimento') {

                field = `${field}Codigo`
            }
            else if (!['ano', 'comInconsistencia'].includes(field)) {

                field = `${field}Id`
            }

            const fields = [
                'ano',
                'competenciaId',
                'unidadeId',
                'ocupacaoId',
                'usuarioId',
                'procedimentoCodigo',
                'cidId',
                'comInconsistencia',
            ]

            fields.slice(fields.indexOf(field) + 1, fields.length)
                .forEach(key => this.$set(this.model, key, null))
        },
        search() {

            this.setLoading()

            this.repositories.relatorioBpaIndividualizado.index(this.prepareData())
                .then(({ data }) => {

                    this.itens = data.procedimentos
                    this.totalizadores = data.totalizadores
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {

            this.form = this.clone(this.initials.form)
            this.model = this.clone(this.initials.model)

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {

            this.setLoading()

            this.repositories.impressao.relatorioBpaIndividualizado(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {

            this.setLoading()

            this.repositories.exportacao.relatorioBpaIndividualizado(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        prepareDataWithColumns() {

            const data = this.prepareData()

            data.sortBy = this.sortBy ? this.camelToSnake(this.sortBy) : null
            data.sortDesc = this.sortDesc

            data.selectedCols = this.fields.map(f => ({
                key: this.camelToSnake(f.key),
                label: f.label,
                class: f.class
            }))

            return data
        },
        prepareData() {

            const data = this.clone(this.model)

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        selectAll(selected) {

            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
        rowClass(item, type) {

            if (type === 'row' && item && !!item.comInconsistenciaValue) {

                return 'table-danger'
            }
        },
        showInconsistencias(bpaProcedimento) {

            this.setLoading()

            this.repositories.relatorioBpaIndividualizado.getInconsistencias(bpaProcedimento.id)
                .then(({ data }) => this.$refs.ModalInconsistencia.open(data))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
    },
    mixins: [repository, error, object, stringCase, file, formats],
    watch: {
        model: {
            deep: true,
            handler() {

                this.itens = []
                this.totalizadores = null
            }
        },
        selectedCols(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
    }
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 400px;
}

.table-resultado >>> .table-footer {
    color: var(--gray-dark);
    font-weight: 600;
    background: #f1f1f1;
    padding: 0.5rem;
    border: 1px solid #dee2e6;
}
</style>